import { useEffect, useState } from "react";
import { adminGqlRequest } from "@/lib/graphql";

const ADMIN_DASHBOARD_QUERY = `
  query AdminDashboard {
    adminDashboard {
      sellers {
        totalCount
        activeCount
        suspendedCount
        closedCount
        newToday
        dayOnDayChangePct
      }
      listings {
        totalCount
        activeCount
        inactiveCount
        newToday
        dayOnDayChangePct
        dayOnDayActiveChangePct
        byProvider {
          provider
          totalCount
          activeCount
        }
      }
      orders {
        totalCount
        pendingCount
        confirmedCount
        shippedCount
        deliveredCount
        cancelledCount
        returnedCount
        processedCount
        todayCount
        yesterdayCount
        dayOnDayChangePct
        totalRevenue
        todayRevenue
        sevenDayRevenue
        dayOnDayRevenueChangePct
        avgOrderValue
        byStatus {
          status
          count
        }
        byProvider {
          provider
          count
        }
        dailyBreakdown {
          date
          count
          revenue
        }
      }
      users {
        totalCount
        activeCount
        pendingCount
        inactiveCount
        newToday
        dayOnDayChangePct
      }
      subscriptions {
        totalActive
        trialing
        pastDue
        byPlan {
          plan
          count
        }
      }
      connections {
        totalCount
        connectedCount
        degradedCount
        disconnectedCount
        byProvider {
          provider
          count
        }
      }
      recentSync {
        failedLast24h
        runningCount
        recentJobs {
          provider
          syncType
          status
          itemsProcessed
          startedAt
          finishedAt
          errorMessage
          workspaceId
        }
      }
    }
  }
`;

export interface AdminDashboardData {
  sellers: {
    totalCount: number;
    activeCount: number;
    suspendedCount: number;
    closedCount: number;
    newToday: number;
    dayOnDayChangePct: number;
  };
  listings: {
    totalCount: number;
    activeCount: number;
    inactiveCount: number;
    newToday: number;
    dayOnDayChangePct: number;
    dayOnDayActiveChangePct: number;
    byProvider: { provider: string; totalCount: number; activeCount: number }[];
  };
  orders: {
    totalCount: number;
    pendingCount: number;
    confirmedCount: number;
    shippedCount: number;
    deliveredCount: number;
    cancelledCount: number;
    returnedCount: number;
    processedCount: number;
    todayCount: number;
    yesterdayCount: number;
    dayOnDayChangePct: number;
    totalRevenue: number;
    todayRevenue: number;
    sevenDayRevenue: number;
    dayOnDayRevenueChangePct: number;
    avgOrderValue: number;
    byStatus: { status: string; count: number }[];
    byProvider: { provider: string; count: number }[];
    dailyBreakdown: { date: string; count: number; revenue: number }[];
  };
  users: {
    totalCount: number;
    activeCount: number;
    pendingCount: number;
    inactiveCount: number;
    newToday: number;
    dayOnDayChangePct: number;
  };
  subscriptions: {
    totalActive: number;
    trialing: number;
    pastDue: number;
    byPlan: { plan: string; count: number }[];
  };
  connections: {
    totalCount: number;
    connectedCount: number;
    degradedCount: number;
    disconnectedCount: number;
    byProvider: { provider: string; count: number }[];
  };
  recentSync: {
    failedLast24h: number;
    runningCount: number;
    recentJobs: {
      provider: string;
      syncType: string;
      status: string;
      itemsProcessed: number;
      startedAt: string | null;
      finishedAt: string | null;
      errorMessage: string | null;
      workspaceId: string;
    }[];
  };
}

export function useAdminDashboard() {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetch() {
    setLoading(true);
    setError(null);
    try {
      const result = await adminGqlRequest<{
        adminDashboard: AdminDashboardData;
      }>(ADMIN_DASHBOARD_QUERY);
      setData(result.adminDashboard);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetch();
  }, []);

  return { data, loading, error, refetch: fetch };
}
